import { CheckmarkCircle, Star, X as XIcon } from '@ui/components/icons';
import { Portal } from '@ui/lib/park/portal';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { useTranslation } from 'next-i18next';
import { Box, Flex, HStack, styled, VStack } from 'styled-system/jsx';

import { useVipModalStore } from './store/useVipModalStore';

// VIP gold identity accent — badge/accent text only, never chrome (V2 §3)
const VIP_GOLD = 'v2.vipGold';
const VIP_MANAGER_NAME = 'Allan | LeverUp';
const VIP_MANAGER_TG_HANDLE = '@allanonchain';
const VIP_MANAGER_TG_LINK = 'https://t.me/allanonchain';

export const VipModal = () => {
  const { t } = useTranslation();
  const isOpen = useVipModalStore((s) => s.isOpen);
  const close = useVipModalStore((s) => s.close);
  const dismissHeaderButton = useVipModalStore((s) => s.dismissHeaderButton);

  const handleMessageManager = () => {
    // Reaching out is the signal the user has engaged — stop nudging them
    // via the header-bar entry from now on.
    dismissHeaderButton();
    window.open(VIP_MANAGER_TG_LINK, '_blank', 'noopener,noreferrer');
  };

  const benefits = [
    t('vip.benefit.support', 'Dedicated support'),
    t('vip.benefit.priority', 'Priority handling'),
    t('vip.benefit.rebates', 'Fee rebates'),
    t('vip.benefit.limits', 'Higher trading limits'),
    t('vip.benefit.campaigns', 'Exclusive campaigns'),
  ];

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      modal={false}
      open={isOpen}
      type="desktopModal_mobileDrawer"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            p="0"
            color="v2.text.primary"
            minW={{ base: 'full', desktop: '420px' }}
            maxW={{ base: 'full', desktop: '520px' }}
            position="relative"
          >
            <Dialog.Title asChild>
              <Flex
                position="relative"
                justifyContent="center"
                alignItems="center"
                p="16px"
                borderBottom="1px solid"
                borderColor="v2.border.default"
              >
                <HStack alignItems="center" gap="8px">
                  <Icon boxSize="28px" color={VIP_GOLD}>
                    <Star />
                  </Icon>
                  <Text textStyle="headings.large">
                    {t('vip.modalTitle', "You're Invited to VIP")}
                  </Text>
                </HStack>
                <Dialog.CloseTrigger
                  position="absolute"
                  right="16px"
                  top="16px"
                  boxShadow="none"
                  bg="none"
                  onClick={() => close()}
                >
                  <Icon w="20px" h="20px">
                    <XIcon />
                  </Icon>
                </Dialog.CloseTrigger>
              </Flex>
            </Dialog.Title>

            <Dialog.Description asChild>
              <VStack alignItems="stretch" gap="16px" p="16px">
                {/* Congrats — highlighted callout */}
                <Box
                  bg="v2.bg.surface1"
                  border="1px solid"
                  borderColor="v2.border.default"
                  borderRadius="4px"
                  px="14px"
                  py="12px"
                >
                  <Text
                    textStyle="headings.xlarge"
                    color="v2.text.primary"
                    textAlign="center"
                    lineHeight="1.35"
                  >
                    {t(
                      'vip.congratsPrefix',
                      'Congratulations, your account is now ',
                    )}
                    <styled.span color={VIP_GOLD}>
                      {t('vip.vipLabel', 'VIP')}
                    </styled.span>
                  </Text>
                </Box>

                {/* Benefits — centered pill chips */}
                <VStack alignItems="stretch" gap="12px">
                  <Text
                    textStyle="text.small"
                    color="v2.text.muted"
                    textAlign="center"
                  >
                    {t(
                      'vip.benefitsLead',
                      "As a VIP trader, you're eligible for:",
                    )}
                  </Text>
                  <Flex flexWrap="wrap" justifyContent="center" gap="8px">
                    {benefits.map((benefit) => (
                      <HStack
                        key={benefit}
                        gap="6px"
                        px="12px"
                        py="6px"
                        borderRadius="2px"
                        bg="v2.bg.surface2"
                        border="1px solid"
                        borderColor="v2.border.default"
                      >
                        <Icon boxSize="14px" color={VIP_GOLD} flexShrink={0}>
                          <CheckmarkCircle />
                        </Icon>
                        <Text
                          textStyle="text.small"
                          color="v2.text.primary"
                          whiteSpace="nowrap"
                        >
                          {benefit}
                        </Text>
                      </HStack>
                    ))}
                  </Flex>
                </VStack>

                {/* Unlock prompt */}
                <Text
                  textStyle="text.default"
                  color="v2.text.secondary"
                  textAlign="center"
                >
                  {t(
                    'vip.unlock',
                    'Message our VIP Manager to confirm your access and unlock your benefits.',
                  )}
                </Text>

                <HStack
                  justifyContent="space-between"
                  alignItems="center"
                  bg="v2.bg.surface1"
                  border="1px solid"
                  borderColor="v2.border.default"
                  borderRadius="4px"
                  px="12px"
                  py="10px"
                >
                  <Text textStyle="text.small" color="v2.text.muted">
                    {t('vip.manager', 'VIP Manager')}
                  </Text>
                  <Box textAlign="right">
                    <Text textStyle="text.default" color="v2.text.primary">
                      {VIP_MANAGER_NAME}
                    </Text>
                    <Text textStyle="text.xsmall" color={VIP_GOLD}>
                      {VIP_MANAGER_TG_HANDLE}
                    </Text>
                  </Box>
                </HStack>

                <Button
                  variant="solid"
                  colorPalette="buttonPrimary"
                  h="44px"
                  gap="8px"
                  onClick={handleMessageManager}
                >
                  {t('vip.cta', 'Message VIP Manager')}
                </Button>
              </VStack>
            </Dialog.Description>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
